import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { blogAPI } from '../api/blogApi';
import type {
  Blog,
  Category,
  Post,
  CreateBlogRequest,
  UpdateBlogRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreatePostRequest,
  UpdatePostRequest,
  PaginationParams,
} from '../types';

interface BlogState {
  // Current blog being viewed
  currentBlog: Blog | null;
  currentBlogCategories: Category[];
  currentBlogPosts: Post[];

  // User's blogs (for profile page)
  myBlogs: Blog[];
  myBlogsLoading: boolean;
  myBlogsError: string | null;

  // Current post being viewed/edited
  currentPost: Post | null;

  // Loading states
  loading: boolean;
  error: string | null;

  // Pagination for posts
  postsPage: number;
  postsPageSize: number;
  postsHasMore: boolean;
  postsLoading: boolean;

  // Pagination (legacy - for other entities)
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
}

const initialState: BlogState = {
  currentBlog: null,
  currentBlogCategories: [],
  currentBlogPosts: [],
  myBlogs: [],
  myBlogsLoading: false,
  myBlogsError: null,
  currentPost: null,
  loading: false,
  error: null,
  postsPage: 1,
  postsPageSize: 5,
  postsHasMore: true,
  postsLoading: false,
  pagination: {
    page: 1,
    pageSize: 20,
    total: 0,
  },
};

// ========== ASYNC THUNKS ==========

// Blogs
export const fetchMyBlogs = createAsyncThunk(
  'blog/fetchMyBlogs',
  async (params?: PaginationParams) => {
    const response = await blogAPI.admin.listMyBlogs(params);
    return response;
  }
);

export const createBlog = createAsyncThunk(
  'blog/createBlog',
  async (data: CreateBlogRequest) => {
    const response = await blogAPI.admin.createBlog(data);
    return response.data;
  }
);

export const updateBlog = createAsyncThunk(
  'blog/updateBlog',
  async ({ id, data }: { id: number; data: UpdateBlogRequest }) => {
    const response = await blogAPI.admin.updateBlog(id, data);
    return response.data;
  }
);

export const deleteBlog = createAsyncThunk('blog/deleteBlog', async (id: number) => {
  await blogAPI.admin.deleteBlog(id);
  return id;
});

export const addBlogAdmin = createAsyncThunk(
  'blog/addBlogAdmin',
  async ({ blogId, email }: { blogId: number; email: string }) => {
    await blogAPI.admin.addBlogAdmin(blogId, email);
    return { blogId, email };
  }
);

export const removeBlogAdmin = createAsyncThunk(
  'blog/removeBlogAdmin',
  async ({ blogId, email }: { blogId: number; email: string }) => {
    await blogAPI.admin.removeBlogAdmin(blogId, email);
    return { blogId, email };
  }
);

export const transferBlogOwnership = createAsyncThunk(
  'blog/transferBlogOwnership',
  async ({ blogId, email }: { blogId: number; email: string }) => {
    const response = await blogAPI.admin.transferBlogOwnership(blogId, email);
    return response.data;
  }
);

export const fetchBlogBySlug = createAsyncThunk(
  'blog/fetchBlogBySlug',
  async (slug: string) => {
    const response = await blogAPI.public.getBlogBySlug(slug);
    return response.data;
  }
);

export const fetchBlogById = createAsyncThunk(
  'blog/fetchBlogById',
  async (id: number) => {
    const response = await blogAPI.public.getBlog(id);
    return response.data;
  }
);

// Categories
export const fetchBlogCategories = createAsyncThunk(
  'blog/fetchBlogCategories',
  async ({ blogId, params }: { blogId: number; params?: PaginationParams }) => {
    const response = await blogAPI.public.getBlogCategories(blogId, params);
    return response.data;
  }
);

export const createCategory = createAsyncThunk(
  'blog/createCategory',
  async (data: CreateCategoryRequest) => {
    const response = await blogAPI.admin.createCategory(data);
    return response.data;
  }
);

export const updateCategory = createAsyncThunk(
  'blog/updateCategory',
  async ({ id, data }: { id: number; data: UpdateCategoryRequest }) => {
    const response = await blogAPI.admin.updateCategory(id, data);
    return response.data;
  }
);

export const deleteCategory = createAsyncThunk('blog/deleteCategory', async (id: number) => {
  await blogAPI.admin.deleteCategory(id);
  return id;
});

// Posts
export const fetchBlogPosts = createAsyncThunk(
  'blog/fetchBlogPosts',
  async ({
    blogId,
    params,
  }: {
    blogId: number;
    params?: PaginationParams & { status?: string; category_id?: number };
  }) => {
    const response = await blogAPI.public.getBlogPosts(blogId, params);
    return response;
  }
);

export const fetchPostBySlug = createAsyncThunk(
  'blog/fetchPostBySlug',
  async (slug: string) => {
    const response = await blogAPI.public.getPostBySlug(slug);
    return response.data;
  }
);

export const createPost = createAsyncThunk('blog/createPost', async (data: CreatePostRequest) => {
  const response = await blogAPI.admin.createPost(data);
  return response.data;
});

export const updatePost = createAsyncThunk(
  'blog/updatePost',
  async ({ id, data }: { id: number; data: UpdatePostRequest }) => {
    const response = await blogAPI.admin.updatePost(id, data);
    return response.data;
  }
);

export const deletePost = createAsyncThunk('blog/deletePost', async (id: number) => {
  await blogAPI.admin.deletePost(id);
  return id;
});

// ========== SLICE ==========

const blogSlice = createSlice({
  name: 'blog',
  initialState,
  reducers: {
    clearCurrentBlog: (state) => {
      state.currentBlog = null;
      state.currentBlogCategories = [];
      state.currentBlogPosts = [];
      state.postsPage = 1;
      state.postsHasMore = true;
    },
    clearCurrentPost: (state) => {
      state.currentPost = null;
    },
    clearError: (state) => {
      state.error = null;
      state.myBlogsError = null;
    },
    resetPostsPagination: (state) => {
      state.currentBlogPosts = [];
      state.postsPage = 1;
      state.postsHasMore = true;
    },
  },
  extraReducers: (builder) => {
    // Fetch my blogs
    builder
      .addCase(fetchMyBlogs.pending, (state) => {
        state.myBlogsLoading = true;
        state.myBlogsError = null;
      })
      .addCase(fetchMyBlogs.fulfilled, (state, action) => {
        state.myBlogsLoading = false;
        state.myBlogs = action.payload.data;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchMyBlogs.rejected, (state, action) => {
        state.myBlogsLoading = false;
        state.myBlogsError = action.error.message || 'Failed to fetch blogs';
      });

    // Create blog
    builder
      .addCase(createBlog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBlog.fulfilled, (state, action) => {
        state.loading = false;
        state.myBlogs.unshift(action.payload);
      })
      .addCase(createBlog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create blog';
      });

    // Update blog
    builder
      .addCase(updateBlog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBlog.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.myBlogs.findIndex((b) => b.id === action.payload.id);
        if (index !== -1) {
          state.myBlogs[index] = action.payload;
        }
        if (state.currentBlog?.id === action.payload.id) {
          state.currentBlog = action.payload;
        }
      })
      .addCase(updateBlog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update blog';
      });

    // Delete blog
    builder
      .addCase(deleteBlog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBlog.fulfilled, (state, action) => {
        state.loading = false;
        state.myBlogs = state.myBlogs.filter((b) => b.id !== action.payload);
        if (state.currentBlog?.id === action.payload) {
          state.currentBlog = null;
        }
      })
      .addCase(deleteBlog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete blog';
      });

    // Transfer blog ownership
    builder.addCase(transferBlogOwnership.fulfilled, (state, action) => {
      const updatedBlog = action.payload;
      // Update currentBlog if it matches
      if (state.currentBlog?.id === updatedBlog.id) {
        state.currentBlog = updatedBlog;
      }
      // Update myBlogs if it contains this blog
      const blogIndex = state.myBlogs.findIndex((b) => b.id === updatedBlog.id);
      if (blogIndex !== -1) {
        state.myBlogs[blogIndex] = updatedBlog;
      }
    });

    // Fetch blog by slug
    builder
      .addCase(fetchBlogBySlug.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBlogBySlug.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBlog = action.payload;
      })
      .addCase(fetchBlogById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBlogById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBlog = action.payload;
      })
      .addCase(fetchBlogById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch blog';
      })
      .addCase(fetchBlogBySlug.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch blog';
      });

    // Fetch blog categories
    builder
      .addCase(fetchBlogCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBlogCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBlogCategories = action.payload;
      })
      .addCase(fetchBlogCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch categories';
      });

    // Create category
    builder
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBlogCategories.push(action.payload);
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create category';
      });

    // Update category
    builder
      .addCase(updateCategory.fulfilled, (state, action) => {
        const index = state.currentBlogCategories.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.currentBlogCategories[index] = action.payload;
        }
      });

    // Delete category
    builder.addCase(deleteCategory.fulfilled, (state, action) => {
      state.currentBlogCategories = state.currentBlogCategories.filter(
        (c) => c.id !== action.payload
      );
    });

    // Fetch blog posts with pagination
    builder
      .addCase(fetchBlogPosts.pending, (state) => {
        state.postsLoading = true;
      })
      .addCase(fetchBlogPosts.fulfilled, (state, action) => {
        state.postsLoading = false;
        const newPosts = action.payload.data;

        // Get existing post IDs to prevent duplicates
        const existingIds = new Set(state.currentBlogPosts.map(p => p.id));

        // Filter out duplicates from new posts
        const uniqueNewPosts = newPosts.filter(post => !existingIds.has(post.id));

        // Append only unique new posts (for load more)
        state.currentBlogPosts = [...state.currentBlogPosts, ...uniqueNewPosts];

        // Update hasMore based on returned data
        state.postsHasMore = newPosts.length >= state.postsPageSize;

        // Increment page for next load
        if (newPosts.length > 0) {
          state.postsPage += 1;
        }
      })
      .addCase(fetchBlogPosts.rejected, (state, action) => {
        state.postsLoading = false;
        state.error = action.error.message || 'Failed to fetch posts';
      });

    // Fetch post by slug
    builder
      .addCase(fetchPostBySlug.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPostBySlug.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPost = action.payload;
      })
      .addCase(fetchPostBySlug.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch post';
      });

    // Create post
    builder
      .addCase(createPost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPost.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBlogPosts.unshift(action.payload);
      })
      .addCase(createPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create post';
      });

    // Update post
    builder
      .addCase(updatePost.fulfilled, (state, action) => {
        const index = state.currentBlogPosts.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.currentBlogPosts[index] = action.payload;
        }
        if (state.currentPost?.id === action.payload.id) {
          state.currentPost = action.payload;
        }
      });

    // Delete post
    builder.addCase(deletePost.fulfilled, (state, action) => {
      state.currentBlogPosts = state.currentBlogPosts.filter((p) => p.id !== action.payload);
      if (state.currentPost?.id === action.payload) {
        state.currentPost = null;
      }
    });
  },
});

export const { clearCurrentBlog, clearCurrentPost, clearError, resetPostsPagination } = blogSlice.actions;
export default blogSlice.reducer;
